import { type NuxtAxiosInstance } from "@nuxtjs/axios";

import { useApi } from "@/utils/api";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useRetailerProductLinksApi
 *
 * @desc        A composable that exposes functions used to interact with the
 *              `consumerProductService` API. The functions are wrappers around
 *              the useApi composable's request function. The returned functions
 *              can be destructured without losing any functionality.
 *
 * @summary     A composable used to interact with the consumerProductService API.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @example <caption>Exposed actions of `useRetailerProductLinksApi`</caption>
 * ```ts
 *   const _api = useRetailerProductLinksApi();
 *   const { get } = _api;
 *
 *   const { data: retailerProductLinkResponse } = await get({ id, hlc });
 * ```
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

export default function useRetailerProductLinksApi($axios: NuxtAxiosInstance) {
  const _api = useApi({ name: "consumerProductService", url: "/v1/retailer_product_links", $axios });

  async function get({ id, hlc }: GetRetailerProductLinkParams) {
    return await _api.request<OneRetailerProductLinkResponse>({
      method: "GET",
      url: `${_api.url}/${id}`,
      params: {
        hlc,
      },
    });
  }

  return { get };
}
